import { computed, inject } from '@angular/core';

import { withDevtools } from '@ngrx-toolkit/core';
import { patchState, signalMethod, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';

import { HasFeatureFlagFacade } from '@kros-sk/core/feature-flags';

import { ApplicationPerson } from './application-persons.types';

const CURRENT_PERSON_STORAGE_KEY = 'kros-current-person-id';

const defaultPersonId = 'default';

const mockApplicationPersons: ApplicationPerson[] = [
  {
    id: 'fero-maly',
    name: 'Fero Malý',
    subtitle: 'Malý podnikateľ',
    email: 'fero.maly@maly-podnikatel.sk',
  },
  {
    id: 'viktor-velky',
    name: 'Viktor Veľký',
    subtitle: 'Veľký podnikateľ',
    email: 'viktor.velky@velky-podnikatel.sk',
  },
  {
    id: 'jana-uctovna',
    name: 'Jana Účtovná',
    subtitle: 'Účtovníčka',
    email: 'jana.uctovna@uctovnicka.sk',
  },
  {
    id: 'zuzka-zamestnana',
    name: 'Zuzka Zamestnaná',
    subtitle: 'Zamestnankyňa',
    email: 'zuzka.zamestnana@zamestnankyna.sk',
  },
  {
    id: 'peter-novy',
    name: 'Peter Nový',
    subtitle: 'Podnikateľ bez služieb',
    email: 'peter.novy@bezsluzby.sk',
  },
  {
    id: 'default',
    name: 'Bez persony',
    subtitle: '',
    email: '',
  },
];

interface ApplicationPersonsState {
  currentPersonId: string | null;
  isLoading: boolean;
  error: unknown | null;
}

const initialState = Object.freeze<ApplicationPersonsState>({
  currentPersonId: 'peter-novy',
  isLoading: false,
  error: null,
});

export const ApplicationPersonsStore = signalStore(
  { providedIn: 'root' },

  withDevtools('Application Persons'),

  withState(initialState),

  withEntities<ApplicationPerson>(),

  withProps(() => {
    const featureFlags = inject(HasFeatureFlagFacade);
    return { _showFeatureFlags: featureFlags.hasFeatureFlagSignal('ShowPersonsChooser') };
  }),

  withComputed((store) => ({
    showPersons: computed(() => store._showFeatureFlags()),
  })),
  withComputed((store) => ({
    currentPerson: computed(() => {
      const id = store.showPersons() ? store.currentPersonId() : defaultPersonId;
      return id ? store.entityMap()[id] : null;
    }),
  })),

  withMethods((store) => {
    return {
      setCurrentPersonId: signalMethod<string | null>((id) => {
        patchState(store, { currentPersonId: id });

        if (id) {
          localStorage.setItem(CURRENT_PERSON_STORAGE_KEY, id);
        } else {
          localStorage.removeItem(CURRENT_PERSON_STORAGE_KEY);
        }
      }),
    };
  }),

  withHooks((store) => {
    return {
      onInit: (): void => {
        patchState(store, setAllEntities(mockApplicationPersons));

        const savedPersonId = store.showPersons() ? localStorage.getItem(CURRENT_PERSON_STORAGE_KEY) : defaultPersonId;
        if (savedPersonId && store.entityMap()[savedPersonId]) {
          patchState(store, { currentPersonId: savedPersonId });
        }
      },
    };
  })
);
